import React from "react";

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
  variant?: "default" | "primary" | "success" | "warning" | "danger" | "info" | "secondary";
  textColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  onDoubleClick?: () => void;
  className?: string;
}

const VARIANT_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  default: { bg: "#ffffff", text: "#333333", border: "#333333" },
  primary: { bg: "#ddf4ff", text: "#0969da", border: "#b6e3ff" },
  success: { bg: "#f0f9f4", text: "#1f883d", border: "#a4dbb5" },
  warning: { bg: "#fef3c7", text: "#d97706", border: "#fcd34d" },
  danger: { bg: "#fee2e2", text: "#dc2626", border: "#fca5a5" },
  info: { bg: "#e0f2fe", text: "#0284c7", border: "#7dd3fc" },
  secondary: { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db" },
};

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge({
  children,
  variant = "default",
  textColor,
  backgroundColor,
  borderColor,
  style = {},
  onClick,
  onDoubleClick,
  className = "",
  ...rest
}, ref) {
  const variantStyle = VARIANT_STYLES[variant] || VARIANT_STYLES.default;

  const badgeStyle: React.CSSProperties = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "1.5",
    border: `1px solid ${borderColor || variantStyle.border}`,
    backgroundColor: backgroundColor || variantStyle.bg,
    color: textColor || variantStyle.text,
    cursor: onClick || onDoubleClick ? "pointer" : "default",
    ...style,
  };

  return (
    <span 
      ref={ref}
      className={`badge ${className}`} 
      style={badgeStyle} 
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      {...rest}
    >
      {children}
    </span>
  );
});

export default Badge;
